import type { EventWithConfig } from '@/api/types/processedEntities'
import { environment, theme } from '@/helpers/Environment'
import type { NormalizedThemeOptions } from '@/themeConfig/types'

export interface TixImage {
  url: string | void
  alt: string
  noCropping: boolean
}

export function imageData(event: EventWithConfig, type: 'teaser' | 'cover' | 'cart'): TixImage {
  let metaAltText: string | undefined = ''
  let imageUrl: string | undefined = ''
  let noCropping: boolean = false

  switch (type) {
    case 'teaser':
      imageUrl = teaserImageUrl(event)
      metaAltText = event.meta?.image_profile_alt_text
      noCropping = event.meta?.image_profile_no_cropping === 'true' ? true : false
      break
    case 'cover':
      imageUrl = coverImageUrl(event)
      metaAltText = event.meta?.image_cover_alt_text
      noCropping = event.meta?.image_cover_no_cropping === 'true' ? true : false
      break
    case 'cart':
      imageUrl = cartItemImageUrl(event)
      metaAltText = event.meta?.image_profile_alt_text
      noCropping = event.meta?.image_profile_no_cropping === 'true' ? true : false
      break
  }

  return {
    url: imageUrl,
    alt: metaAltText ?? `Image for ${event.name}`,
    noCropping: noCropping ?? false,
  }
}

/**
 * Use the same sizes for each type of image to reduce data use and increase cache hits.
 */
function teaserImageUrl(event: EventWithConfig): string | undefined {
  const { width, height } = teaserImageSize()
  return event.meta?.image_profile_no_cropping === 'true'
    ? imageUrl(event.meta.image_profile, width * 2)
    : imageUrl(event.meta.image_profile, width * 2, height * 2)
}

export function tenantTeaserImageSize(size?: NormalizedThemeOptions['teaserImageSize']): {
  width: number
  height: number
} {
  // Most tenants have teasers that are 272x216px, an aspect ratio of 4:5.
  const width = size?.width ?? 272
  const height = size?.height ?? 216
  return { width, height }
}

export function teaserImageSize() {
  return tenantTeaserImageSize(theme?.teaserImageSize)
}

function coverImageUrl(event: EventWithConfig): string | undefined {
  return imageUrl(event.meta.image_cover, 1500)
}

export function imageUrlMaxWidth(url: string): string | void {
  return imageUrl(url, 1500)
}

function cartItemImageUrl(event: EventWithConfig): string | undefined {
  const width = 64
  const height = 51
  if (event.meta?.image_profile_no_cropping !== 'true') {
    return imageUrl(event.meta.image_profile, width * 2, height * 2)
  }
}

function imageUrl(apiUrl: string, width: number, height?: number) {
  if (apiUrl) {
    // Use a relative URL to load the image from the CDN instead of the API.
    // Also replace the path prefix from /v1/ to /api/ to request via the tix-web API proxy.
    const path = apiUrl.replace(/^https?:\/\/.*?\/v1\//, '/api/')

    const parameters = {
      sizex: width,
      sizey: height,
    }

    const queryString = Object.entries(parameters)
      .filter(([, value]) => Boolean(value))
      .map(([name, value]) => `${name}=${value}`)
      .join('&')

    return path + '?shrinkonly&' + queryString
  }
}

export interface LogoURLs {
  large: string
  small?: string
}

export function getLogoUrls(slug: string): LogoURLs {
  function format(logo?: string) {
    if (logo) {
      return `themes/${logo}`
    }
  }

  const logo = environment.manifest[`${slug}/logo`]
  const logoSmall = environment.manifest[`${slug}/logoSmall`]
  const sandboxLogo = environment.manifest['sandbox/logo']
  return {
    large: format(logo ?? sandboxLogo)!,
    small: format(logoSmall),
  }
}
